'use client';

import { useEffect, useRef, useState } from 'react';

function parseStatValue(raw) {
  const str = String(raw);
  const match = str.match(/^([\d.]+)(.*)$/);
  if (!match) return { end: null, suffix: str, decimals: 0 };
  const end = parseFloat(match[1]);
  const suffix = match[2] || '';
  const decimals = match[1].includes('.') ? match[1].split('.')[1].length : 0;
  return { end, suffix, decimals };
}

/**
 * Animates a numeric stat once when scrolled into view (IntersectionObserver).
 */
export function useAnimatedStat(value, { duration = 1500, threshold = 0.6 } = {}) {
  const ref = useRef(null);
  const { end, suffix, decimals } = parseStatValue(value);
  const [display, setDisplay] = useState(value);
  const done = useRef(false);

  useEffect(() => {
    if (end == null || Number.isNaN(end)) return;

    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || done.current) return;
        done.current = true;

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          setDisplay(value);
          return;
        }

        const start = performance.now();
        const step = (now) => {
          const t = Math.min(1, (now - start) / duration);
          const eased = 1 - (1 - t) ** 3;
          const current = end * eased;
          const formatted =
            decimals > 0 ? current.toFixed(decimals) : String(Math.round(current));
          setDisplay(`${formatted}${suffix}`);
          if (t < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      },
      { threshold },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [value, end, suffix, decimals, duration, threshold]);

  return { ref, display };
}
